'use client'

import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, Link2, User, QrCode, Lock, BarChart3, Palette, Copy, Check, Plus, Trash2, ExternalLink, Sparkles, Download, Users, Globe, ClipboardList, ShoppingBag, Gift } from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { useUser } from '@clerk/nextjs'
import QRCodeStyling from 'qr-code-styling'
import { toast } from 'sonner'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { ShinyButton } from '@/components/ui/shiny-button'
import SlideSwapButton from '@/components/ui/slide-swap-button'

// ── Types ──────────────────────────────────────────────────────────────────

interface GuestLink {
    id: string
    originalUrl: string
    slug: string
    createdAt: string
}

interface GuestQR {
    id: string
    url: string
    createdAt: string
}

// ── Helpers ────────────────────────────────────────────────────────────────

const LIMITS = { links: 5, qr: 2 }
const LS_KEY = { links: 'll_guest_links', qr: 'll_guest_qr', counts: 'll_guest_counts' }

function randSlug() {
    return Math.random().toString(36).slice(2, 8)
}

function isValidUrl(url: string) {
    try {
        new URL(url)
        return true
    } catch {
        return false
    }
}

// ── Sub-component: Locked overlay ─────────────────────────────────────────

function LockedBlock({ label }: { label: string }) {
    return (
        <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 overflow-hidden">
            <div className="absolute inset-0 backdrop-blur-[2px] bg-black/40 z-10 flex flex-col items-center justify-center gap-4 rounded-2xl">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-primary" />
                </div>
                <p className="text-white/70 font-semibold text-sm text-center max-w-xs">
                    {label} is available for registered users.
                </p>
                <Link
                    href="/auth/sign-up"
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-full text-sm font-bold hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(0,102,255,0.3)]"
                >
                    Sign Up Free <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </div>
            {/* Blurred placeholder content */}
            <div className="blur-sm select-none pointer-events-none space-y-3">
                <div className="h-4 bg-white/10 rounded w-3/4" />
                <div className="h-4 bg-white/10 rounded w-1/2" />
                <div className="h-20 bg-white/5 rounded-xl mt-4" />
                <div className="h-8 bg-white/10 rounded-full w-32" />
            </div>
        </div>
    )
}

// ── Sub-component: Usage Bar ───────────────────────────────────────────────

function UsageBar({ label, used, max, color }: { label: string; used: number; max: number; color: string }) {
    const pct = Math.min((used / max) * 100, 100)
    return (
        <div className="flex flex-col gap-1.5 min-w-[120px]">
            <div className="flex items-center justify-between">
                <span className="text-white/40 text-xs font-mono uppercase tracking-widest">{label}</span>
                <span className={`text-xs font-bold ${used >= max ? 'text-red-400' : 'text-white/60'}`}>{used}/{max}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                    className={`h-full rounded-full ${color}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.5 }}
                />
            </div>
        </div>
    )
}

const QRPreview = forwardRef(({ url, id }: { url: string; id: string }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const qrRef = useRef<QRCodeStyling | null>(null)

    useEffect(() => {
        if (!containerRef.current || !url) return
        containerRef.current.innerHTML = ''
        const qr = new QRCodeStyling({
            width: 160, height: 160,
            data: url,
            dotsOptions: { color: '#ffffff', type: 'rounded' },
            backgroundOptions: { color: 'transparent' },
            cornersSquareOptions: { color: '#0066ff' },
        })
        qr.append(containerRef.current)
        qrRef.current = qr
    }, [url])

    useImperativeHandle(ref, () => ({
        download: () => {
            if (qrRef.current) {
                qrRef.current.download({ name: `qr-${id}`, extension: 'png' })
            }
        }
    }))

    if (!url) return (
        <div className="w-40 h-40 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            <QrCode className="w-10 h-10 text-white/20" />
        </div>
    )

    return (
        <div className="relative group/qr inline-block">
            <div ref={containerRef} className="rounded-xl overflow-hidden bg-white/5 p-2" />
        </div>
    )
})
QRPreview.displayName = 'QRPreview'

function QRCard({ q, onDelete }: { q: GuestQR; onDelete: (id: string) => void }) {
    const qrRef = useRef<{ download: () => void } | null>(null)

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="sharp-edge rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 flex flex-col items-center gap-5 group hover:bg-white/[0.04] transition-all"
        >
            <QRPreview ref={qrRef} url={q.url} id={q.id} />
            <div className="text-center w-full">
                <p className="text-white/60 text-xs truncate max-w-[240px] mx-auto">{q.url}</p>
            </div>
            <div className="flex gap-2 w-full flex-wrap">
                <button
                    onClick={() => qrRef.current?.download()}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary/20 hover:bg-primary/30 text-primary text-xs font-bold transition-all"
                >
                    <Download className="w-3.5 h-3.5" /> Download
                </button>
                <a
                    href={q.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/40 hover:text-white text-xs font-medium transition-all"
                >
                    <ExternalLink className="w-3.5 h-3.5" /> Visit
                </a>
                <button
                    onClick={() => onDelete(q.id)}
                    className="w-full sm:flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 hover:bg-red-500/20 text-white/40 hover:text-red-400 text-xs font-medium transition-all"
                >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
            </div>
        </motion.div>
    )
}

// ── Main Page ─────────────────────────────────────────────────────────────

type Tab = 'links' | 'qr'

export default function CreateFreePage() {
    const router = useRouter()
    const { isLoaded, isSignedIn } = useUser()
    const [tab, setTab] = useState<Tab>('links')

    // ── Short Links state ──
    const [links, setLinks] = useState<GuestLink[]>([])
    const [linkInput, setLinkInput] = useState('')
    const [linkError, setLinkError] = useState('')
    const [copiedId, setCopiedId] = useState<string | null>(null)

    // ── QR state ──
    const [qrList, setQrList] = useState<GuestQR[]>([])
    const [qrInput, setQrInput] = useState('')
    const [qrError, setQrError] = useState('')

    // ── Dynamic URL state ──
    const [baseUrl, setBaseUrl] = useState('https://Lenklyst.app')
    const [displayHost, setDisplayHost] = useState('Lenklyst.app')

    // ── Usage Counts ──
    const [counts, setCounts] = useState({ links: 0, qr: 0 })

    // ── Redirect authenticated users to dashboard ──
    useEffect(() => {
        if (isLoaded && isSignedIn) {
            router.replace('/dashboard')
        }
    }, [isLoaded, isSignedIn, router])

    // ── Load from localStorage ──
    useEffect(() => {
        let envUrl = process.env.NEXT_PUBLIC_BASE_URL || window.location.origin
        // forcefully remove any trailing slashes to prevent `//r/[slug]`
        envUrl = envUrl.replace(/\/+$/, '')

        setBaseUrl(envUrl)
        setDisplayHost(envUrl.replace(/^https?:\/\//, ''))
        try {
            const now = new Date().getTime()
            const isNotExpired = (item: { createdAt: string }) => {
                const created = new Date(item.createdAt).getTime()
                return now - created < 24 * 60 * 60 * 1000 // 24 hours
            }

            let loadedLinks = 0
            const ls = localStorage.getItem(LS_KEY.links)
            if (ls) {
                const parsed = JSON.parse(ls)
                const valid = parsed.filter(isNotExpired)
                setLinks(valid)
                loadedLinks = parsed.length
                if (valid.length !== parsed.length) localStorage.setItem(LS_KEY.links, JSON.stringify(valid))
            }

            let loadedQr = 0
            const lq = localStorage.getItem(LS_KEY.qr)
            if (lq) {
                const parsed = JSON.parse(lq)
                const valid = parsed.filter(isNotExpired)
                setQrList(valid)
                loadedQr = parsed.length
                if (valid.length !== parsed.length) localStorage.setItem(LS_KEY.qr, JSON.stringify(valid))
            }

            const lc = localStorage.getItem(LS_KEY.counts)
            if (lc) {
                setCounts(JSON.parse(lc))
            } else {
                const initCounts = { links: loadedLinks, qr: loadedQr }
                setCounts(initCounts)
                localStorage.setItem(LS_KEY.counts, JSON.stringify(initCounts))
            }
        } catch { }
    }, [])

    // ── Short Link handlers ──
    const addLink = () => {
        setLinkError('')
        if (!linkInput.trim()) return setLinkError('Please enter a URL.')
        try { new URL(linkInput) } catch { return setLinkError('Please enter a valid URL (include https://).') }
        if (counts.links >= LIMITS.links) return setLinkError(`You've reached the guest limit of ${LIMITS.links} links. Sign up for unlimited!`)
        const entry: GuestLink = { id: randSlug(), originalUrl: linkInput.trim(), slug: randSlug(), createdAt: new Date().toISOString() }
        const next = [...links, entry]
        setLinks(next)
        localStorage.setItem(LS_KEY.links, JSON.stringify(next))
        const nextCounts = { ...counts, links: counts.links + 1 }
        setCounts(nextCounts)
        localStorage.setItem(LS_KEY.counts, JSON.stringify(nextCounts))
        setLinkInput('')
        toast.success('Short link generated successfully!')
    }

    const deleteLink = (id: string) => {
        const next = links.filter(l => l.id !== id)
        setLinks(next)
        localStorage.setItem(LS_KEY.links, JSON.stringify(next))
    }

    const copyLink = (slug: string, id: string) => {
        navigator.clipboard.writeText(`${baseUrl}/r/${slug}`)
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
    }

    // ── QR handlers ──
    const addQR = () => {
        setQrError('')
        if (!qrInput.trim()) return setQrError('Please enter a URL.')
        try { new URL(qrInput) } catch { return setQrError('Please enter a valid URL (include https://).') }
        if (counts.qr >= LIMITS.qr) return setQrError(`You've reached the guest limit of ${LIMITS.qr} QR codes. Sign up for more!`)
        const entry: GuestQR = { id: randSlug(), url: qrInput.trim(), createdAt: new Date().toISOString() }
        const next = [...qrList, entry]
        setQrList(next)
        localStorage.setItem(LS_KEY.qr, JSON.stringify(next))
        const nextCounts = { ...counts, qr: counts.qr + 1 }
        setCounts(nextCounts)
        localStorage.setItem(LS_KEY.counts, JSON.stringify(nextCounts))
        setQrInput('')
        toast.success('QR Code generated successfully!')
    }

    const deleteQR = (id: string) => {
        const next = qrList.filter(q => q.id !== id)
        setQrList(next)
        localStorage.setItem(LS_KEY.qr, JSON.stringify(next))
    }

    const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
        { id: 'links', label: 'Short Links', icon: <Link2 className="w-4 h-4" /> },
        { id: 'qr', label: 'QR Codes', icon: <QrCode className="w-4 h-4" /> },
    ]

    return (
        <div className="relative min-h-screen bg-black overflow-hidden selection:bg-primary/30">
            <GeometricBackground />

            <MarketingNavbar />

            <div className="max-w-[1000px] 2xl:max-w-[1400px] mx-auto px-6 lg:px-12 pt-40 pb-32 relative z-10">

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-10"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-primary/80 text-xs font-semibold tracking-[0.25em] uppercase">Try It Free — No Sign Up Required</span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl 2xl:text-8xl font-bold tracking-tighter text-white mb-4 leading-none">
                        GENERATE.<br /><span className="text-gray-600">INSTANTLY.</span>
                    </h1>
                    <p className="text-white/50 text-base max-w-xl leading-relaxed">
                        Create short links or generate QR codes, no account needed.
                        Sign up to unlock analytics, custom pages, and higher generation limits with custom or no expiry.
                    </p>
                </motion.div>

                {/* Usage bar */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="sharp-edge rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 mb-8 flex flex-wrap gap-6 items-center justify-between"
                >
                    <div className="flex flex-wrap gap-6 flex-1">
                        <UsageBar label="Links" used={counts.links} max={LIMITS.links} color="bg-primary" />
                        <UsageBar label="QR Codes" used={counts.qr} max={LIMITS.qr} color="bg-emerald-500" />
                    </div>
                    <ShinyButton 
                        onClick={() => router.push('/auth/sign-up')}
                        className="whitespace-nowrap"
                    >
                        Increase Your Limits <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </ShinyButton>
                </motion.div>

                {/* Tabs */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex gap-2 mb-6 p-1 sharp-edge rounded-full bg-white/[0.03] border border-white/[0.06] w-fit"
                >
                    {tabs.map(t => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === t.id ? 'bg-primary text-white shadow-[0_0_20px_rgba(0,102,255,0.3)]' : 'text-white/40 hover:text-white'
                                }`}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </motion.div>

                <AnimatePresence mode="wait">

                    {/* ── Short Links Tab ── */}
                    {tab === 'links' && (
                        <motion.div key="links" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">

                            {/* Input */}
                            <div className="sharp-edge rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
                                <label className="text-white/60 text-sm font-medium mb-3 block">Paste a long URL to shorten</label>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <input
                                        value={linkInput}
                                        onChange={e => setLinkInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && addLink()}
                                        placeholder="https://example.com/very-long-url"
                                        className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 text-sm outline-none focus:border-primary/50 transition-colors"
                                    />
                                    <SlideSwapButton
                                        text="Shorten"
                                        loadingText="Shortening..."
                                        successText="Shortened!"
                                        icon={Plus}
                                        onClick={addLink}
                                        disabled={counts.links >= LIMITS.links || !linkInput.trim() || !isValidUrl(linkInput)}
                                        classes="w-full sm:w-auto"
                                    />
                                </div>
                                <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                                    <span className="text-white/40 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Guest links expire after 24 hours and include a 10s preview.</span>
                                    <Link href="/auth/sign-up" className="text-primary hover:text-primary/80 transition-colors font-medium">Sign up for permanent links →</Link>
                                </div>
                                {linkError && <p className="text-red-400 text-xs mt-2">{linkError}</p>}
                                {counts.links >= LIMITS.links && (
                                    <div className="mt-3 flex items-center gap-2 text-amber-400 text-xs">
                                        <Lock className="w-3.5 h-3.5" />
                                        Guest limit reached.{' '}
                                        <Link href="/auth/sign-up" className="underline underline-offset-2 hover:text-white transition-colors">
                                            Sign up for higher link limits →
                                        </Link>
                                    </div>
                                )}
                            </div>

                            {/* Generated links list */}
                            {links.length === 0 ? (
                                <div className="text-center py-12 text-white/20">
                                    <Link2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                    <p className="text-sm">Your shortened links will appear here.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {links.map(l => (
                                        <motion.div
                                            key={l.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className="sharp-edge rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3 sm:p-4 flex items-center gap-3 sm:gap-4 group hover:bg-white/[0.04] transition-all"
                                        >
                                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <Link2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-primary font-mono text-xs sm:text-sm font-semibold truncate">{displayHost}/r/{l.slug}</p>
                                                <p className="text-white/30 text-[10px] sm:text-xs truncate">{l.originalUrl}</p>
                                            </div>
                                            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                                                <button
                                                    onClick={() => copyLink(l.slug, l.id)}
                                                    className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-all focus:ring-1 focus:ring-primary/20 outline-none"
                                                    title="Copy link"
                                                >
                                                    {copiedId === l.id ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400" /> : <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                                                </button>
                                                <button
                                                    onClick={() => deleteLink(l.id)}
                                                    className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-white/5 hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-all focus:ring-1 focus:ring-red-500/20 outline-none"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}

                            {/* Locked analytics */}
                            <div className="mt-6">
                                <p className="text-white/30 text-xs font-mono uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <Lock className="w-3 h-3" /> Analytics — Sign up to unlock
                                </p>
                                <LockedBlock label="Click analytics, geo data & device tracking" />
                            </div>
                        </motion.div>
                    )}

                    {/* ── QR Tab ── */}
                    {tab === 'qr' && (
                        <motion.div key="qr" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">

                            {/* Input */}
                            <div className="sharp-edge rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
                                <label className="text-white/60 text-sm font-medium mb-3 block">Enter a URL to generate a QR code</label>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <input
                                        value={qrInput}
                                        onChange={e => setQrInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && addQR()}
                                        placeholder="https://example.com"
                                        className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/20 text-sm outline-none focus:border-primary/50 transition-colors"
                                    />
                                    <SlideSwapButton
                                        text="Generate"
                                        loadingText="Generating..."
                                        successText="Generated!"
                                        icon={QrCode}
                                        onClick={addQR}
                                        disabled={counts.qr >= LIMITS.qr || !qrInput.trim() || !isValidUrl(qrInput)}
                                        classes="w-full sm:w-auto"
                                    />
                                </div>
                                <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                                    <span className="text-white/40 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Guest QR codes expire after 24 hours.</span>
                                    <Link href="/auth/sign-up" className="text-primary hover:text-primary/80 transition-colors font-medium">Sign up for permanent QR codes →</Link>
                                </div>
                                {qrError && <p className="text-red-400 text-xs mt-2">{qrError}</p>}
                                {counts.qr >= LIMITS.qr && (
                                    <div className="mt-3 flex items-center gap-2 text-amber-400 text-xs">
                                        <Lock className="w-3.5 h-3.5" />
                                        Guest limit reached.{' '}
                                        <Link href="/auth/sign-up" className="underline underline-offset-2 hover:text-white transition-colors">
                                            Sign up for more QR codes →
                                        </Link>
                                    </div>
                                )}
                            </div>

                            {/* QR cards */}
                            {qrList.length === 0 ? (
                                <div className="text-center py-12 text-white/20">
                                    <QrCode className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                    <p className="text-sm">Your QR codes will appear here.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {qrList.map(q => (
                                        <QRCard key={q.id} q={q} onDelete={deleteQR} />
                                    ))}
                                </div>
                            )}

                            {/* Locked customization */}
                            <div className="mt-6">
                                <p className="text-white/30 text-xs font-mono uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <Palette className="w-3 h-3" /> Custom Colors & Logo — Sign up to unlock
                                </p>
                                <LockedBlock label="Custom QR colors, logo embedding, and scan analytics" />
                            </div>
                        </motion.div>
                    )}

                </AnimatePresence>

                {/* Bottom CTA banner */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-16 sharp-edge rounded-3xl border border-primary/20 bg-primary/[0.04] p-10 flex flex-col sm:flex-row items-center justify-between gap-6"
                >
                    <div className="w-full">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-10">
                            <div>
                                <h3 className="text-white text-3xl font-bold tracking-tighter mb-2">Want the full experience?</h3>
                                <p className="text-white/40 text-sm leading-relaxed max-w-xl">
                                    Sign up for free to transform your basic links into a powerful marketing engine. 
                                    Lenklyst is built as a complete branding ecosystem for the modern creator.
                                </p>
                            </div>
                            <Link
                                href="/auth/sign-up"
                                className="flex items-center gap-2 px-8 py-4 bg-primary text-white rounded-full font-bold hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_40px_rgba(0,102,255,0.3)] whitespace-nowrap"
                            >
                                Sign Up Free <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                { 
                                    icon: <Link2 className="w-5 h-5 text-primary" />, 
                                    title: "Smart Link Management", 
                                    desc: "Permanent links with higher generation limits and custom expiry options." 
                                },
                                { 
                                    icon: <Palette className="w-5 h-5 text-primary" />, 
                                    title: "Personal Branding", 
                                    desc: "Build stunning Link-in-Bio pages (Mobile Hub) to showcase your social ecosystem." 
                                },
                                { 
                                    icon: <BarChart3 className="w-5 h-5 text-primary" />, 
                                    title: "Advanced Analytics", 
                                    desc: "Track every click with detailed geo-location, device, and referral data." 
                                },
                                { 
                                    icon: <QrCode className="w-5 h-5 text-primary" />, 
                                    title: "QR Innovation", 
                                    desc: "Generate custom QR codes with your brand colors and embedded logos." 
                                },
                                { 
                                    icon: <Sparkles className="w-5 h-5 text-primary" />, 
                                    title: "Retargeting Support", 
                                    desc: "Integrate FB, Google, and LinkedIn pixels to grow and track your audience." 
                                },
                                { 
                                    icon: <Users className="w-5 h-5 text-primary" />, 
                                    title: "Team Collaboration", 
                                    desc: "Invite team members, manage roles, and collaborate on branding assets in real-time." 
                                },
                                { 
                                    icon: <Globe className="w-5 h-5 text-primary" />, 
                                    title: "Custom Domain", 
                                    desc: "Connect your own domain to maintain consistent branding across all your short links." 
                                },
                                { 
                                    icon: <ClipboardList className="w-5 h-5 text-primary" />, 
                                    title: "Forms & Polls", 
                                    desc: "Collect high-quality leads and feedback with integrated forms and interactive polls." 
                                },
                                { 
                                    icon: <ShoppingBag className="w-5 h-5 text-primary" />, 
                                    title: "Digital Product Store", 
                                    desc: "Sell your digital assets, downloads, and courses directly from your mobile hub." 
                                },
                                { 
                                    icon: <Gift className="w-5 h-5 text-primary" />, 
                                    title: "Referral Features", 
                                    desc: "Launch powerful referral and reward campaigns to grow your network organically." 
                                },
                                { 
                                    icon: <User className="w-5 h-5 text-primary" />, 
                                    title: "Marketing Ecosystem", 
                                    desc: "A unified workspace built for creators, marketers, and startup founders." 
                                },
                            ].map((feature, idx) => (
                                <motion.div 
                                    key={idx}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.05 }}
                                    viewport={{ once: true }}
                                    className="flex gap-4"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.08] flex items-center justify-center shrink-0">
                                        {feature.icon}
                                    </div>
                                    <div>
                                        <h4 className="text-white/90 text-sm font-bold mb-1">{feature.title}</h4>
                                        <p className="text-white/30 text-xs leading-relaxed">{feature.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                <MarketingFooter />
            </div>
        </div >
    )
}
